// ============================================================================
// BP-04-02: Documents Frontend - useDocumentUpload Hook
// ============================================================================

import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { documentsApi } from '../api/documentsApi';
import { useDocumentsStore } from '../stores/documentsStore';
import { documentKeys } from './useDocuments';
import type {
  Document,
  DocumentCreate,
  DocumentType,
  UploadProgress,
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
} from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface UploadOptions {
  familyId: string;
  defaultMetadata?: Partial<DocumentCreate>;
  onUploadStart?: (file: File) => void;
  onUploadProgress?: (file: File, progress: number) => void;
  onUploadComplete?: (file: File, document: Document) => void;
  onUploadError?: (file: File, error: Error) => void;
  onAllComplete?: (documents: Document[], errors: Array<{ file: File; error: Error }>) => void;
}

interface UseDocumentUploadReturn {
  uploadFile: (file: File, metadata?: Partial<DocumentCreate>) => Promise<Document | null>;
  uploadFiles: (files: File[], metadata?: Partial<DocumentCreate>) => Promise<void>;
  cancelUpload: (filename: string) => void;
  clearQueue: () => void;
  isUploading: boolean;
  uploadQueue: UploadProgress[];
  totalProgress: number;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const MAX_SIZE = 50 * 1024 * 1024; // 50MB

// -----------------------------------------------------------------------------
// Utility Functions
// -----------------------------------------------------------------------------

function validateFile(file: File): { valid: boolean; error?: string } {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `File type "${file.type}" is not allowed. Allowed types: PDF, Images, Word, Excel, Text, CSV.`,
    };
  }

  if (file.size > MAX_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File size (${sizeMB}MB) exceeds maximum allowed size of 50MB.`,
    };
  }

  return { valid: true };
}

function inferDocumentType(file: File): DocumentType {
  const mimeType = file.type;
  const filename = file.name.toLowerCase();

  // Check filename for hints
  if (filename.includes('iep') || filename.includes('individual education')) {
    return 'iep' as DocumentType;
  }
  if (filename.includes('medical') || filename.includes('diagnosis') || filename.includes('doctor')) {
    return 'medical_record' as DocumentType;
  }
  if (filename.includes('insurance') || filename.includes('claim')) {
    return 'insurance' as DocumentType;
  }
  if (filename.includes('legal') || filename.includes('court') || filename.includes('attorney')) {
    return 'legal' as DocumentType;
  }
  if (filename.includes('therapy') || filename.includes('pt') || filename.includes('ot')) {
    return 'therapy' as DocumentType;
  }
  if (filename.includes('school') || filename.includes('report card')) {
    return 'school' as DocumentType;
  }

  // Check mime type
  if (mimeType === 'application/pdf') {
    return 'medical_record' as DocumentType; // Default PDF to medical
  }

  return 'other' as DocumentType;
}

function generateTitle(file: File): string {
  // Remove extension and clean up
  const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
  // Replace underscores and dashes with spaces
  const cleaned = nameWithoutExt.replace(/[_-]/g, ' ');
  // Title case
  return cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

export function useDocumentUpload(options: UploadOptions): UseDocumentUploadReturn {
  const {
    familyId,
    defaultMetadata,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError,
    onAllComplete,
  } = options;

  const queryClient = useQueryClient();
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const {
    uploadQueue,
    isUploading,
    addToUploadQueue,
    removeFromUploadQueue,
    updateUploadProgress,
    clearUploadQueue,
    setIsUploading,
  } = useDocumentsStore();

  // Calculate total progress
  const totalProgress =
    uploadQueue.length > 0
      ? Math.round(
          uploadQueue.reduce((sum, item) => sum + item.progress, 0) / uploadQueue.length
        )
      : 0;

  // Upload single file
  const uploadFile = useCallback(
    async (file: File, metadata?: Partial<DocumentCreate>): Promise<Document | null> => {
      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        const error = new Error(validation.error);
        onUploadError?.(file, error);
        updateUploadProgress(file.name, { status: 'error', error: validation.error });
        return null;
      }

      // Add to queue
      addToUploadQueue(file);
      updateUploadProgress(file.name, { status: 'uploading' });
      onUploadStart?.(file);

      // Create abort controller
      const abortController = new AbortController();
      abortControllers.current.set(file.name, abortController);

      try {
        // Build metadata
        const uploadMetadata: DocumentCreate = {
          document_type: metadata?.document_type || defaultMetadata?.document_type || inferDocumentType(file),
          title: metadata?.title || defaultMetadata?.title || generateTitle(file),
          folder_id: metadata?.folder_id ?? defaultMetadata?.folder_id ?? null,
          child_id: metadata?.child_id ?? defaultMetadata?.child_id ?? null,
          description: metadata?.description || defaultMetadata?.description,
          document_date: metadata?.document_date || defaultMetadata?.document_date,
          provider_name: metadata?.provider_name || defaultMetadata?.provider_name,
          tags: metadata?.tags || defaultMetadata?.tags || [],
        };

        // Upload with progress tracking
        const document = await documentsApi.upload(
          familyId,
          file,
          uploadMetadata,
          (progress) => {
            updateUploadProgress(file.name, { progress });
            onUploadProgress?.(file, progress);
          }
        );

        // Mark complete
        updateUploadProgress(file.name, {
          status: 'complete',
          progress: 100,
          document_id: document.id,
        });
        onUploadComplete?.(file, document);

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
        queryClient.invalidateQueries({ queryKey: documentKeys.stats(familyId) });

        return document;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Upload failed');
        updateUploadProgress(file.name, { status: 'error', error: err.message });
        onUploadError?.(file, err);
        return null;
      } finally {
        abortControllers.current.delete(file.name);
      }
    },
    [
      familyId,
      defaultMetadata,
      addToUploadQueue,
      updateUploadProgress,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      onUploadError,
      queryClient,
    ]
  );

  // Upload multiple files
  const uploadFiles = useCallback(
    async (files: File[], metadata?: Partial<DocumentCreate>): Promise<void> => {
      setIsUploading(true);

      const documents: Document[] = [];
      const errors: Array<{ file: File; error: Error }> = [];

      // Upload files concurrently (max 3 at a time)
      const CONCURRENCY = 3;
      const queue = [...files];

      const uploadNext = async (): Promise<void> => {
        const file = queue.shift();
        if (!file) return;

        const result = await uploadFile(file, metadata);
        if (result) {
          documents.push(result);
        } else {
          errors.push({
            file,
            error: new Error(
              uploadQueue.find((u) => u.file.name === file.name)?.error || 'Upload failed'
            ),
          });
        }

        await uploadNext();
      };

      // Start concurrent uploads
      await Promise.all(
        Array(Math.min(CONCURRENCY, files.length))
          .fill(null)
          .map(() => uploadNext())
      );

      setIsUploading(false);
      onAllComplete?.(documents, errors);
    },
    [uploadFile, setIsUploading, uploadQueue, onAllComplete]
  );

  // Cancel upload
  const cancelUpload = useCallback(
    (filename: string) => {
      const controller = abortControllers.current.get(filename);
      if (controller) {
        controller.abort();
        abortControllers.current.delete(filename);
      }
      removeFromUploadQueue(filename);
    },
    [removeFromUploadQueue]
  );

  // Clear queue
  const clearQueue = useCallback(() => {
    // Abort all pending uploads
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current.clear();
    clearUploadQueue();
  }, [clearUploadQueue]);

  return {
    uploadFile,
    uploadFiles,
    cancelUpload,
    clearQueue,
    isUploading,
    uploadQueue,
    totalProgress,
  };
}

// -----------------------------------------------------------------------------
// Drag and Drop Utilities
// -----------------------------------------------------------------------------

export function isValidDropItem(item: DataTransferItem): boolean {
  if (item.kind !== 'file') return false;
  return ALLOWED_TYPES.includes(item.type);
}

export function getFilesFromDragEvent(event: React.DragEvent): File[] {
  const files: File[] = [];
  
  if (event.dataTransfer.items) {
    for (let i = 0; i < event.dataTransfer.items.length; i++) {
      const item = event.dataTransfer.items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && ALLOWED_TYPES.includes(file.type)) {
          files.push(file);
        }
      }
    }
  } else if (event.dataTransfer.files) {
    for (let i = 0; i < event.dataTransfer.files.length; i++) {
      const file = event.dataTransfer.files[i];
      if (ALLOWED_TYPES.includes(file.type)) {
        files.push(file);
      }
    }
  }
  
  return files;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default useDocumentUpload;


